/**
 * Renders checklist step instructions as Markdown (links, images, basic formatting).
 * All supported links open in a new browser tab so the checklist run is not navigated away.
 * Azure / ops-stored image URLs use authenticated fetch; other URLs render as normal images.
 */

import React, { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { clsx } from "clsx";
import { AuthenticatedImage } from "./AuthenticatedImage";
import {
  isChecklistInstructionInternalPathHref,
  safeChecklistInstructionExternalHref,
} from "./checklistInstructionLinks";

function isOpsBlobImageUrl(src: string | undefined | null): boolean {
  if (!src) return false;
  if (src.includes("blob.core.windows.net")) return true;
  if (/^https?:\/\//i.test(src) && src.includes("ops-checklists")) return true;
  return false;
}

function isSafeImageSrc(src: string | undefined | null): boolean {
  if (!src) return false;
  return /^https?:\/\//i.test(src);
}

interface ChecklistInstructionsMarkdownProps {
  content: string;
  className?: string;
  /** Smaller text and image previews (e.g. checklist config editor). */
  compact?: boolean;
}

const imgClassDefault =
  "max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-600";
const imgClassCompact =
  "max-w-full max-h-32 w-auto h-auto object-contain rounded-lg border border-slate-200 dark:border-slate-600";

function normalizeBulletChars(src: string): string {
  if (!src) return "";
  /** Unicode bullets pasted from docs → Markdown `- ` so lists render with discs. */
  return src.replace(/^[•·▪▸]\s*/gm, "- ");
}

/**
 * GitHub-style soft line breaks without `remark-breaks` (avoids Vite optimize-deps churn).
 * Appends two spaces before single newlines so CommonMark renders `<br>`.
 * Skips blank lines, fenced code, and lines where the next line starts a block/list.
 */
function injectMarkdownHardBreaks(src: string): string {
  const lines = src.split("\n");
  let inFence = false;

  const startsBlockOrList = (s: string) =>
    /^\s*(#{1,6}\s|>|```|\||\[)|^\s*([-*+] |\d+\.\s)/.test(s);

  const isStructuralLine = (s: string) =>
    /^\s*(#{1,6}\s|>|```|\|)|^\s*([-*+] |\d+\.\s)/.test(s);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trimStart();
    if (t.startsWith("```")) {
      inFence = !inFence;
    }
    const next = lines[i + 1];
    if (
      inFence ||
      next === undefined ||
      line === "" ||
      next === "" ||
      startsBlockOrList(next) ||
      isStructuralLine(line)
    ) {
      continue;
    }
    lines[i] = `${line}  `;
  }
  return lines.join("\n");
}

function ChecklistInstructionsMarkdownInner({
  content,
  className,
  compact = false,
}: ChecklistInstructionsMarkdownProps) {
  const text = injectMarkdownHardBreaks(normalizeBulletChars(content?.trim() ?? ""));
  if (!text) {
    return (
      <p className="text-slate-500 dark:text-slate-400 italic text-sm">No instructions</p>
    );
  }

  const imgCls = compact ? imgClassCompact : imgClassDefault;
  /** Stay phrasing-level — react-markdown wraps images in `<p>`; `block` wrappers break HTML validity & hydration. */
  const imgWrap = compact
    ? "not-prose my-1.5 inline-block max-w-full align-middle"
    : "not-prose my-3 inline-block max-w-full align-middle";

  return (
    <div
      className={clsx(
        "prose prose-sm max-w-none dark:prose-invert",
        compact
          ? "prose-p:my-1 prose-p:leading-snug prose-ul:my-1 prose-ol:my-1 prose-pre:my-1"
          : "prose-p:my-2 prose-p:leading-relaxed prose-ul:my-2 prose-ol:my-2 prose-pre:my-2",
        "prose-a:text-inherit", // we style `a` in the component; avoids fighting prose
        "prose-code:text-slate-800 dark:prose-code:text-slate-200",
        "text-slate-700 dark:text-slate-200",
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          /** `<p>` cannot contain block-level nodes; MD often wraps images in `<p>`. Use `div` for valid DOM + hydration. */
          p({ children }) {
            return (
              <div className={compact ? "my-1 leading-snug" : "my-2 leading-relaxed"}>
                {children}
              </div>
            );
          },
          ul({ children, className: ulClass }) {
            return (
              <ul
                className={clsx(
                  "not-prose my-2 list-disc ps-5 sm:ps-6 text-inherit overflow-visible",
                  "[list-style-position:outside]",
                  compact ? "text-xs space-y-0.5" : "text-sm space-y-1",
                  ulClass,
                )}
              >
                {children}
              </ul>
            );
          },
          ol({ children, className: olClass }) {
            return (
              <ol
                className={clsx(
                  "not-prose my-2 list-decimal ps-5 sm:ps-6 space-y-1 text-inherit",
                  "[list-style-position:outside]",
                  olClass,
                )}
              >
                {children}
              </ol>
            );
          },
          li({ children, className: liClass }) {
            return (
              <li className={clsx("leading-relaxed marker:text-slate-400 dark:marker:text-slate-500", liClass)}>
                {children}
              </li>
            );
          },
          strong({ children }) {
            return (
              <strong className="not-prose font-semibold text-inherit">{children}</strong>
            );
          },
          em({ children }) {
            return <em className="not-prose italic text-inherit">{children}</em>;
          },
          a({ href, children, className: linkClass }) {
            const linkStyles = clsx(
              "not-prose inline font-medium break-words rounded-sm",
              "text-indigo-600 underline decoration-indigo-500/60 underline-offset-2",
              "hover:text-indigo-500 hover:decoration-indigo-500",
              "dark:text-sky-300 dark:decoration-sky-500/50",
              "dark:hover:text-sky-200 dark:hover:decoration-sky-400/70",
              linkClass
            );

            if (href && isChecklistInstructionInternalPathHref(href)) {
              return (
                <a
                  href={href.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkStyles}
                >
                  {children}
                </a>
              );
            }

            const external = safeChecklistInstructionExternalHref(href);
            if (!external) {
              return (
                <span className="text-amber-600/90 text-sm">[link not supported]</span>
              );
            }
            return (
              <a
                href={external}
                target="_blank"
                rel="noopener noreferrer"
                className={linkStyles}
              >
                {children}
              </a>
            );
          },
          img({ src, alt, className: imgClass }) {
            if (!src || !isSafeImageSrc(src)) {
              return (
                <span className="not-prose text-xs text-amber-600 dark:text-amber-400 my-1">
                  [image blocked — use http(s) URL]
                </span>
              );
            }
            if (isOpsBlobImageUrl(src)) {
              return (
                <span className={imgWrap}>
                  <AuthenticatedImage
                    src={src}
                    alt={alt || "Instruction image"}
                    className={clsx(imgCls, imgClass)}
                  />
                </span>
              );
            }
            return (
              <span className={imgWrap}>
                <img
                  src={src}
                  alt={alt || ""}
                  className={clsx(imgCls, imgClass)}
                  loading="lazy"
                />
              </span>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export const ChecklistInstructionsMarkdown = memo(
  ChecklistInstructionsMarkdownInner,
  (a, b) =>
    a.content === b.content &&
    a.compact === b.compact &&
    a.className === b.className
);
ChecklistInstructionsMarkdown.displayName = "ChecklistInstructionsMarkdown";
